import { Cause, Effect, Exit, Layer, Queue, Ref, Schema, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import { makeSessionOwner } from "../state/session-owner.ts";
import { recordingFullscreenFailure, type RecordingFullscreenSnapshot } from "./fullscreen-model.ts";
import { RecordingFullscreenIo, RecordingFullscreenSessions, type RecordingFullscreenSession } from "./fullscreen-service.ts";

export const RecordingFullscreenSessionsLive = (root: Scope.Scope) => Layer.effect(RecordingFullscreenSessions, Effect.gen(function* () {
  const io = yield* RecordingFullscreenIo, registry = yield* AtomRegistry.AtomRegistry, failures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(values => values.length ? Effect.failCause(Cause.fromReasons(values.flatMap(value => value.reasons))) : Effect.void)));
  const sessions = yield* makeSessionOwner(root);
  const acquire = Effect.gen(function* () {
    const owner = yield* Scope.Scope, queue = yield* io.events(root).pipe(Effect.orDie);
    const state = yield* Ref.make<RecordingFullscreenSnapshot>({ active: false, error: null }), read = Ref.get(state), snapshot = Atom.make(read);
    const alive = () => root.state._tag !== "Closed" && owner.state._tag !== "Closed";
    const publish = Effect.sync(() => { if (alive()) registry.refresh(snapshot); });
    const observe = Effect.gen(function* () {
      const observation = yield* Queue.take(queue);
      if (!alive()) return yield* Effect.interrupt;
      const result = yield* Effect.exit(Effect.gen(function* () {
        if (observation._tag === "Failed") return yield* Effect.die(observation.failure);
        const active = yield* Schema.decodeUnknownEffect(Schema.Boolean)(observation.value).pipe(Effect.mapError(recordingFullscreenFailure), Effect.orDie);
        if (alive()) yield* Ref.set(state, { active, error: null });
      }));
      if (Exit.isFailure(result)) {
        if (!result.cause.reasons.some(Cause.isDieReason)) return yield* Effect.failCause(result.cause);
        yield* Ref.update(failures, values => [...values, result.cause]);
        if (alive()) yield* Ref.update(state, value => ({ ...value, error: INTERNAL_HTTP_MESSAGE }));
      }
      yield* publish;
    });
    yield* Effect.forkIn(Effect.forever(observe), owner, { uninterruptible: false });
    return { snapshot, read } satisfies RecordingFullscreenSession;
  }).pipe(Effect.onExit(exit => Exit.isFailure(exit) ? Ref.update(failures, values => [...values, exit.cause]) : Effect.void));
  return RecordingFullscreenSessions.of({ open: sessions.open(acquire) });
}));
